/**
 * TypeScript types mirroring the backend Provider CRUD schema (src/api/schemas/providers.py).
 * All status/type values are lower-case strings to match the Databricks DDL.
 */

export const PROVIDER_TYPES = [
  "broker",
  "mga",
  "tpa",
  "loss_adjuster",
  "coverholder",
  "other",
] as const;

export type ProviderType = (typeof PROVIDER_TYPES)[number];

export const PROVIDER_STATUSES = ["pending", "active", "inactive"] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

/** Human-readable labels for the provider type enum values. */
export const PROVIDER_TYPE_LABELS: Record<ProviderType, string> = {
  broker: "Broker",
  mga: "MGA",
  tpa: "TPA",
  loss_adjuster: "Loss Adjuster",
  coverholder: "Coverholder",
  other: "Other",
};

export interface SchemaAssignmentSummary {
  schema_id: string;
  schema_name: string | null;
}

/** Full provider record as returned by GET /api/v2/providers/{id} and POST /api/v2/providers. */
export interface ProviderOut {
  provider_id: string;
  provider_name: string;
  provider_short_code: string;
  provider_type: ProviderType;
  provider_status: ProviderStatus;
  is_active: boolean;
  fix_override_enabled: boolean;
  auto_promote_to_gold: boolean;
  created_at: string | null;
  updated_at: string | null;
  activated_at: string | null;
  last_submission_at: string | null;
  assigned_schemas: SchemaAssignmentSummary[];
  can_manage: boolean;
}

/** Paginated list response from GET /api/v2/providers. */
export interface ProviderListResponse {
  items: ProviderOut[];
  total: number;
  offset: number;
  limit: number;
}

/** Request body for POST /api/v2/providers. */
export interface ProviderCreate {
  provider_name: string;
  provider_short_code: string;
  provider_type: ProviderType;
}

/** Response from the uniqueness check endpoints. */
export interface UniquenessCheckResponse {
  available: boolean;
}

/** Full schema-assignment record as returned by GET /api/v2/providers/{id}/schema-assignments. */
export interface SchemaAssignmentOut {
  provider_id: string;
  schema_id: string;
  schema_name: string | null;
  is_active: boolean;
  assigned_by: string | null;
  assigned_at: string | null;
}

/** A user linked to a provider, returned by GET /api/v2/providers/{id}/users. */
export interface ProviderUser {
  user_id: string;
  email: string | null;
  access_role: string | null;
  is_active: boolean;
}
